import { useState } from 'react';
import {
    IconSearch, IconX, IconChevronRight, IconArrowLeft,
    IconBriefcase, IconBeach, IconCalendarEvent, IconFirstAidKit
} from '@tabler/icons-react';

const branches = [
    { id: 'hscl', name: 'HSCL', description: 'Health Systems Consult Limited' },
    { id: 'sanhdef', name: 'sanhdef', description: 'Sustainable Action for Health Development Foundation' },
];

const projects = [
    { id: 1, name: 'Pooled Procurement System for Malaria Commodities', branchId: 'hscl', code: 'PPMC-001', plc: 'NG-HSCL-001' },
    { id: 2, name: 'Commercialization Plans for SRH Products', branchId: 'hscl', code: 'DCPS-002', plc: 'NG-HSCL-002' },
    { id: 3, name: 'Regional Health Market Reassessment - SRH', branchId: 'hscl', code: 'RHMA-003', plc: 'NG-HSCL-003' },
    { id: 4, name: 'Family Planning Quantification & Forecasting', branchId: 'hscl', code: 'RQFP-004', plc: 'NG-HSCL-004' },
    { id: 5, name: 'National Nutrition Resource Mobilization', branchId: 'hscl', code: 'NNRM-005', plc: 'NG-HSCL-005' },
    { id: 6, name: 'Kano Health Systems Strengthening', branchId: 'sanhdef', code: 'KHSS-006', plc: 'NG-SDF-001' },
    { id: 7, name: 'Lagos AOP Design and PHC Support', branchId: 'hscl', code: 'LADS-007', plc: 'NG-HSCL-006' },
    { id: 8, name: 'HIV Epidemic Control - Cluster 3', branchId: 'hscl', code: 'ACHE-008', plc: 'NG-HSCL-007', sharedWith: 'sanhdef' },
];

const chargeTypes = [
    { id: 'project_work', name: 'Project Work', description: 'Regular billable project hours', icon: IconBriefcase, isLeave: false },
];

export default function ChargeLookupModal({ isOpen, onClose, onSelect, entryId, projects: propProjects, organizations: propOrganizations }) {
    // Start at Step 1 if organizations are provided, otherwise Step 2 if projects are provided, else Step 1
    const [step, setStep] = useState(propOrganizations && propOrganizations.length > 0 ? 1 : (propProjects ? 2 : 1));
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);

    const projectsList = propProjects || projects;
    const branchesList = propOrganizations || branches;

    if (!isOpen) return null;

    const resetModal = () => {
        setStep(propOrganizations && propOrganizations.length > 0 ? 1 : (propProjects ? 2 : 1));
        setSearchTerm('');
        setSelectedBranch(null);
        setSelectedProject(null);
    };

    const handleClose = () => {
        resetModal();
        onClose();
    };

    const handleBack = () => {
        if (step === 2) {
            if (propProjects) {
                handleClose(); 
                return;
            }
            setSelectedBranch(null);
            setStep(1);
        } else if (step === 3) {
            setSelectedProject(null);
            setStep(2);
        }
        setSearchTerm('');
    };

    const handleBranchSelect = (branch) => {
        setSelectedBranch(branch);
        setSearchTerm('');
        setStep(2);
    };

    const handleProjectSelect = (project) => {
        setSelectedProject(project);
        setSearchTerm('');
        setStep(3);
    };

    const handleChargeTypeSelect = (chargeType) => {
        const selectionData = {
            entryId,
            branch: selectedBranch,
            project: selectedProject,
            chargeType,
            description: chargeType.id === 'project_work' ? selectedProject.name : chargeType.name,
            projectCode: selectedProject.id,
            plc: selectedProject.plc || 'REGULAR',
            payType: chargeType.name,
            isLeave: chargeType.isLeave,
            isTimeOff: chargeType.isTimeOff || false,
            timeOffType: chargeType.timeOffType || null,
            leaveBalance: chargeType.balance || null,
        };
        if (onSelect) onSelect(selectionData);
        handleClose();
    };

    const getFilteredProjects = () => {
        let filtered = projectsList;

        if (selectedBranch) {
            filtered = filtered.filter(p =>
                (p.org === selectedBranch?.id) ||
                (p.branchId === selectedBranch?.id) ||
                (p.sharedWith === selectedBranch?.id)
            );
        }

        if (searchTerm) {
            filtered = filtered.filter(p =>
                p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (p.code && p.code.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
        return filtered;
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-6" onClick={handleClose}>
            <div
                className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-primary px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            {step > 1 && (
                                <button
                                    onClick={handleBack}
                                    className="w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                                >
                                    <IconArrowLeft size={22} className="text-white" />
                                </button>
                            )}
                            <div>
                                <h2 className="text-2xl font-bold text-white">
                                    {step === 1 && 'Select Organization'}
                                    {step === 2 && 'Select Project'}
                                    {step === 3 && 'Select Charge Type'}
                                </h2>
                                <p className="text-white/80 text-sm mt-1">Step {step} of 3</p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                        >
                            <IconX size={22} className="text-white" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="max-h-[500px] overflow-y-auto">

                    {/* Organization */}
                    {step === 1 && (
                        <div className="p-8">
                            <p className="text-gray-500 mb-6 text-base">Choose the organization you're charging time to</p>
                            <div className="space-y-4">
                                {branchesList.map((branch) => (
                                    <button
                                        key={branch.id}
                                        onClick={() => handleBranchSelect(branch)}
                                        className="w-full flex items-center justify-between p-6 bg-gray-50 rounded-xl hover:bg-success-50 border-2 border-transparent hover:border-org-green-primary transition-all group"
                                    >
                                        <div className="text-left">
                                            <p className="text-xl font-semibold text-gray-900 group-hover:text-org-green-primary">
                                                {branch.name}
                                            </p>
                                            <p className="text-sm text-gray-500 mt-1">{branch.description || branch.code || 'Organization'}</p>
                                        </div>
                                        <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center group-hover:bg-org-green-primary transition-colors">
                                            <IconChevronRight size={24} className="text-gray-400 group-hover:text-white" />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Project */}
                    {step === 2 && (
                        <div>
                            <div className="p-8 pb-4">
                                <div className="flex items-center gap-3 mb-5">
                                    <span className="px-4 py-2 rounded-full text-sm font-medium text-white bg-org-green-primary">
                                        {selectedBranch?.name}
                                    </span>
                                    <span className="text-gray-400">|</span>
                                    <span className="text-gray-500">{getFilteredProjects().length} projects</span>
                                </div>
                                <div className="relative">
                                    <IconSearch size={22} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search by project name or code..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        autoFocus
                                        className="w-full pl-14 pr-4 py-4 bg-gray-50 border-2 border-gray-100 rounded-xl text-base focus:outline-none focus:border-org-green-primary focus:bg-white transition-colors"
                                    />
                                </div>
                            </div>
                            <div className="px-8 pb-8">
                                {getFilteredProjects().length === 0 ? (
                                    <div className="text-center py-16 text-gray-400">
                                        <p className="text-lg">No projects found</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {getFilteredProjects().map((project) => (
                                            <button
                                                key={project.id}
                                                onClick={() => handleProjectSelect(project)}
                                                className="w-full flex items-center justify-between p-5 bg-gray-50 rounded-xl hover:bg-success-50 border-2 border-transparent hover:border-org-green-primary transition-all group text-left"
                                            >
                                                <div className="flex-grow pr-4">
                                                    <p className="font-medium text-gray-900 text-base group-hover:text-org-green-primary">
                                                        {project.name}
                                                    </p>
                                                    <p className="text-sm text-gray-400 mt-2 font-mono">{project.code}</p>
                                                </div>
                                                <IconChevronRight size={22} className="text-gray-300 group-hover:text-org-green-primary flex-shrink-0" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/*  Charge Type */}
                    {step === 3 && (
                        <div className="p-8">
                            <div className="mb-8 p-5 rounded-xl border-2 border-org-green-primary/20 bg-success-50">
                                <div className="flex items-center gap-3 mb-3">
                                    <span className="px-3 py-1.5 rounded-full text-xs font-medium text-white bg-org-green-primary">
                                        {selectedBranch?.name}
                                    </span>
                                    <span className="text-sm text-gray-400 font-mono">{selectedProject?.code}</span>
                                </div>
                                <p className="font-medium text-gray-900 text-base">{selectedProject?.name}</p>
                            </div>

                            <p className="text-gray-500 mb-5 text-base">How should this time be charged?</p>

                            <div className="space-y-4">
                                {chargeTypes.map((chargeType) => {
                                    const ChargeIcon = chargeType.icon;
                                    return (
                                        <button
                                            key={chargeType.id}
                                            onClick={() => handleChargeTypeSelect(chargeType)}
                                            className="w-full flex items-center gap-5 p-5 bg-gray-50 rounded-xl hover:bg-success-50 border-2 border-transparent hover:border-org-green-primary transition-all group"
                                        >
                                            <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white bg-org-green-primary">
                                                <ChargeIcon size={28} />
                                            </div>
                                            <div className="flex-grow text-left">
                                                <p className="font-semibold text-gray-900 text-base group-hover:text-org-green-primary">
                                                    {chargeType.name}
                                                </p>
                                                <p className="text-sm text-gray-500 mt-1">{chargeType.description}</p>
                                            </div>
                                            {chargeType.balance !== undefined && (
                                                <span className="px-4 py-2 bg-success-50 text-org-green-primary text-sm font-semibold rounded-full border border-org-green-primary/30">
                                                    {chargeType.balance} days
                                                </span>
                                            )}
                                            <IconChevronRight size={22} className="text-gray-300 group-hover:text-org-green-primary" />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
